import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  apiGetMedicationsMedicationGroupsOptions,
  type GDMTInfo,
  type ListMedicationGroupsResponse,
  type MedicationEntry,
} from "../api";
import { queryClient } from "../api/query-client";

const COMBINATION_DOSE_UNIT = "mg";

/** A medication with its group — the flat view the fuzzy matcher searches over. */
export interface MedicationRef {
  group: string;
  name: string;
}

/**
 * Supported medications — the backend-owned catalog of medications and their
 * groups, fetched once per session. The nested map is only ever consumed through
 * lookups, so (like `Countries`, unlike the array-shaped catalogs)
 * `useSupportedMedications` returns a `SupportedMedications` instance exposing
 * them.
 */
export class SupportedMedications {
  private itemsCache?: MedicationRef[];

  constructor(
    private readonly catalog: ListMedicationGroupsResponse["medications"],
  ) {}

  /** Group names, in backend order. */
  get groups(): string[] {
    return Object.keys(this.catalog);
  }

  namesForGroup(group: string): string[] {
    return Object.keys(this.catalog[group]?.medications ?? {});
  }

  /**
   * GDMT scoring metadata for a drug class, or `undefined` when the class isn't
   * part of the score. Carries the per-class point ceiling, which HF types it
   * counts for, and the shared RAAS slot.
   */
  gdmtInfo(group: string): GDMTInfo | undefined {
    return this.catalog[group]?.gdmt ?? undefined;
  }

  /** The drug classes that carry a GDMT score, in backend order. */
  get gdmtGroups(): string[] {
    return this.groups.filter((group) => this.gdmtInfo(group) != null);
  }

  /** The medication's metadata, or `undefined` for a plain drug / unknown name. */
  findItem(group: string, name: string): MedicationEntry | undefined {
    return this.catalog[group]?.medications[name] ?? undefined;
  }

  combinationDose(
    group: string | null | undefined,
    name: string | null | undefined,
  ): { medication_dose: number; medication_unit: string } | undefined {
    if (!group || !name) return undefined;
    const components = this.findItem(group, name)?.components;
    if (components == null) return undefined;
    return {
      medication_dose: components.reduce((sum, c) => sum + c.dose, 0),
      medication_unit: COMBINATION_DOSE_UNIT,
    };
  }

  allowsFreeText(group: string): boolean {
    return this.catalog[group]?.free_text ?? false;
  }

  /** Whether `name` is a catalog entry of `group` (vs a free-text medication). */
  isKnownMedication(group: string, name: string): boolean {
    return this.namesForGroup(group).includes(name);
  }

  /** Groups whose medications can be free-texted (for classifying a custom entry). */
  get freeTextGroups(): string[] {
    return this.groups.filter((group) => this.allowsFreeText(group));
  }

  /** Flat (group, name) view for the fuzzy matcher; built once per instance. */
  get items(): MedicationRef[] {
    return (this.itemsCache ??= Object.entries(this.catalog).flatMap(
      ([group, schema]) =>
        Object.keys(schema.medications).map((name) => ({ group, name })),
    ));
  }
}

function supportedMedicationsQuery() {
  return { ...apiGetMedicationsMedicationGroupsOptions(), staleTime: Infinity };
}

/**
 * Suspends the nearest boundary until loaded, so callers always get a ready
 * instance. Memoized on the referentially-stable query data.
 */
export function useSupportedMedications(): SupportedMedications {
  const { data } = useSuspenseQuery(supportedMedicationsQuery());
  return useMemo(
    () => new SupportedMedications(data.medications),
    [data.medications],
  );
}

export async function fetchSupportedMedications(): Promise<SupportedMedications> {
  const { medications } = await queryClient.ensureQueryData(
    supportedMedicationsQuery(),
  );
  return new SupportedMedications(medications);
}
